import { FormControlProps, Input, InputProps } from "@chakra-ui/react";
import { useRef } from "react";
import { Controller, ControllerProps, ControllerRenderProps, FieldPath, FieldValues } from "react-hook-form";

import { ControlledFormControl } from "../ControlledFormControl/ControlledFormControl";
import { NumberControlledInput } from "./types/NumberControlledInput";

export type ControlledInputRenderProps<T extends FieldValues, TName extends FieldPath<T>> = Omit<
  ControllerRenderProps<T, TName>,
  "ref"
> &
  Pick<InputProps, "autoFocus" | "background" | "isDisabled" | "placeholder" | "textAlign">;

export interface ControlledInputOptions<T extends FieldValues, TName extends FieldPath<T>> {
  center?: boolean;

  isDisabled?: boolean;

  label?: React.ReactNode;

  render?: (
    properties: ControlledInputRenderProps<T, TName>,
    reference: React.RefObject<HTMLInputElement> | undefined,
  ) => React.ReactElement;

  type?: "number" | string;
}

export interface ControlledInputProperties<T extends FieldValues, TName extends FieldPath<T>>
  extends ControlledInputOptions<T, TName>,
    Pick<ControllerProps<T, TName>, "control" | "defaultValue" | "name" | "rules">,
    Omit<FormControlProps, "defaultValue" | "label"> {
  isFocus?: boolean;
  placeholder?: string;
}

export const ControlledInput = <T extends FieldValues, TName extends FieldPath<T>>({
  type,
  ...properties
}: ControlledInputProperties<T, TName>): React.ReactElement => {
  const inputReference = useRef<HTMLInputElement>(null);

  if (type === "number") {
    const { isFocus, ...withoutFocus } = properties;
    void isFocus;
    return <NumberControlledInput {...withoutFocus} inputRef={inputReference} />;
  }

  const {
    background,
    center = false,
    control,
    defaultValue,
    isDisabled,
    isFocus = false,
    label,
    name,
    placeholder,
    render = ({ value, ...properties_ }) => (
      <Input type={type} value={value} {...properties_} autoComplete="off" autoFocus={isFocus} size={size} />
    ),
    rules,
    size,
    textAlign,
    ...rest
  } = properties;
  return (
    <ControlledFormControl
      control={control}
      isCentered={center}
      isDisabled={isDisabled}
      isRequired={!!rules?.required}
      label={label}
      name={name}
      size={size}
      textAlign={textAlign}
      {...rest}
    >
      <Controller
        control={control}
        defaultValue={defaultValue}
        name={name}
        render={({ field }) => {
          const { ref, ...withoutReference } = field;
          void ref;
          return render(
            {
              autoFocus: isFocus,
              background,
              isDisabled,
              placeholder: placeholder ?? (typeof label === "string" ? label : undefined),
              textAlign,
              ...withoutReference,
            },
            inputReference,
          );
        }}
        rules={rules}
      />
    </ControlledFormControl>
  );
};
